// Clavian Boot server releases (F-BOOT-23; spec/25 §13.6, spec/14 §12): the boot server's own software, updated by hand.
//
//   POST  /v1/hq/boot-server-releases                    register a signed boot server release      hq:releases
//   GET   /v1/hq/boot-server-releases                    every release, and each channel's current  hq:releases
//   PUT   /v1/hq/boot-server-release-channels/{channel}  which release is current on a channel      hq:releases
//   GET   /v1/boot-server/release                        the current release of the server's channel  boot server
//
// **The same trust as a station release** (`station-releases.ts`): a release is a manifest — the ClavianBootServer MSI
// (and, when the release has one, the branded setup exe), each with its https URL, sha256 and size — signed offline by
// the release tool (`tools/scripts/boot-release.mjs`) with the **same Ed25519 release key** as station releases, as a
// compact JWS. Its audience is its own (`aud: clavian-boot-server-release`) and its payload names the product
// (`product: clavian-boot-server`), so a station manifest is never taken for a boot server's, nor the other way round. The
// boot server verifies it against the public keys pinned in its build (the station's) before it downloads a byte, and the
// MSI's sha256 and size before it is offered for install. The server stores the signed form and says which one is
// current; it cannot make a file of its own trusted.
//
// **Manual, never automatic.** A boot server checks the current release of its venue's channel
// (`boot.serverUpdateChannel`, `stable` by default) at start and every 6 hours, downloads and verifies a newer one, and
// waits: nothing installs until staff press Update now (`POST …/boot/servers/{id}/update`, `boot.ts`) in the console or
// on the server's own status page. Nothing is offered until HQ registers a release **and** makes it current on a channel,
// so the feature changes nothing on the day it ships.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_RELEASES } from "../permissions/index.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { BootServerVersionSchema } from "./boot.js";
import { CompactJwsSchema, ReleaseHttpsUrlSchema, Sha256HexSchema } from "./station-releases.js";
const forbidden = problemResponse('Not HQ, no `hq:releases`, or no second factor on the session.', 'forbidden', 'second-factor-required');
// ---------------------------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------------------------
/** The JWS audience of a signed boot server release manifest. */
export const BOOT_SERVER_RELEASE_AUDIENCE = 'clavian-boot-server-release';
/** The `product` a boot server release manifest names. */
export const BOOT_SERVER_RELEASE_PRODUCT = 'clavian-boot-server';
/** The channels a boot server release can be current on. */
export const BOOT_SERVER_RELEASE_CHANNELS = ['stable', 'beta'];
export const BootServerReleaseChannelSchema = z.enum(BOOT_SERVER_RELEASE_CHANNELS).openapi('BootServerReleaseChannel', {
    description: 'A boot server release channel: `stable` (every venue by default) or `beta` (venues that take releases early).',
});
/** The largest file a boot server release may carry (2 GiB). */
export const MAX_BOOT_SERVER_RELEASE_FILE_BYTES = 2 * 1024 * 1024 * 1024;
export const BootServerReleaseFileSchema = z
    .object({
    url: ReleaseHttpsUrlSchema,
    sha256: Sha256HexSchema,
    size: z.int().min(1).max(MAX_BOOT_SERVER_RELEASE_FILE_BYTES),
})
    .openapi('BootServerReleaseFile', { description: 'One file of a boot server release: where it is, its sha256 and its size.' });
/** The manifest the release key signs (the JWS payload, less its `aud`/`iat`). */
export const BootServerReleaseManifestSchema = z
    .object({
    /** Always `clavian-boot-server`: a manifest for anything else is refused. */
    product: z.literal(BOOT_SERVER_RELEASE_PRODUCT),
    version: BootServerVersionSchema,
    /** The channel the release was cut for. Informational: HQ decides where it is current. */
    channel: BootServerReleaseChannelSchema,
    /** The ClavianBootServer MSI. A major upgrade: `boot.json`, the data root and the pairing are kept. */
    msi: BootServerReleaseFileSchema,
    /** The branded first-install setup exe, when the release has one. Not used by an update. */
    setup: BootServerReleaseFileSchema.optional(),
    /** The oldest installed version that may update to this one directly; null when any may. */
    minVersion: BootServerVersionSchema.nullable(),
    releasedAt: IsoDateTimeSchema,
})
    .openapi('BootServerReleaseManifest', {
    description: 'A boot server release: the ClavianBootServer MSI with its sha256 and size, signed with the release key (spec/25 §13.6).',
});
// ---------------------------------------------------------------------------------------------
// HQ
// ---------------------------------------------------------------------------------------------
export const BootServerReleaseSchema = z
    .object({
    version: BootServerVersionSchema,
    channel: BootServerReleaseChannelSchema,
    manifest: BootServerReleaseManifestSchema,
    /** The `kid` of the release key that signed it. */
    keyId: z.string().min(1).max(128),
    /** The channels this release is current on (none, one or both). */
    currentOn: z.array(BootServerReleaseChannelSchema).max(BOOT_SERVER_RELEASE_CHANNELS.length),
    createdAt: IsoDateTimeSchema,
})
    .openapi('BootServerRelease', { description: 'A registered, signed boot server release (spec/25 §13.6).' });
export const BootServerReleaseChannelStateSchema = z
    .object({
    channel: BootServerReleaseChannelSchema,
    /** The release boot servers on this channel are offered; null while none is (nothing is offered). */
    currentVersion: BootServerVersionSchema.nullable(),
    /** When HQ last changed it; null for a channel never set. */
    updatedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('BootServerReleaseChannelState', { description: 'Which boot server release is current on a channel.' });
export const BootServerReleaseListSchema = z
    .object({
    /** Every registered release, newest first (at most 200). */
    data: z.array(BootServerReleaseSchema),
    /** Each channel, `stable` then `beta`. */
    channels: z.array(BootServerReleaseChannelStateSchema),
})
    .openapi('BootServerReleaseList', { description: 'Every registered boot server release, and the release current on each channel.' });
export const RegisterBootServerReleaseSchema = z
    .object({
    /** The manifest as the release tool signed it: a compact JWS, `alg: EdDSA`, `aud: clavian-boot-server-release`. */
    signedManifest: CompactJwsSchema,
})
    .openapi('RegisterBootServerRelease', { description: 'A signed boot server release manifest to register.' });
export const registerBootServerReleaseRoute = createRoute({
    method: 'post',
    path: '/v1/hq/boot-server-releases',
    operationId: 'registerBootServerRelease',
    tags: ['hq'],
    summary: 'Register a signed boot server release (F-BOOT-23)',
    description: 'Stores a boot server release manifest the release tool signed with the Ed25519 release key. The server verifies the ' +
        'signature against the configured release public keys (`STATION_RELEASE_PUBLIC_KEYS` — the same key signs both ' +
        'products), the audience, the product (`clavian-boot-server`) and the manifest before storing it; registering offers ' +
        'it to nobody until it is made current on a channel. One platform audit row. Platform scope: requires `hq:releases` ' +
        'and a second factor.',
    [PERMISSION_EXTENSION]: HQ_RELEASES,
    request: { body: { content: { [JSON_CONTENT_TYPE]: { schema: RegisterBootServerReleaseSchema } }, required: true } },
    responses: {
        201: jsonResponse(BootServerReleaseSchema, 'The release was registered.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        409: problemResponse('A boot server release with this version is already registered.', 'conflict'),
        422: problemResponse('The signature does not verify, no release keys are configured, or the manifest is not a boot server release.', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listBootServerReleasesRoute = createRoute({
    method: 'get',
    path: '/v1/hq/boot-server-releases',
    operationId: 'listBootServerReleases',
    tags: ['hq'],
    summary: 'Every boot server release, and each channel’s current one',
    description: 'The registered boot server releases, newest first (at most 200), and the release current on `stable` and `beta`. Platform scope: requires `hq:releases` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_RELEASES,
    responses: {
        200: jsonResponse(BootServerReleaseListSchema, 'The releases and the channels.'),
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const BootServerReleaseChannelParamsSchema = z.object({ channel: BootServerReleaseChannelSchema }).openapi('BootServerReleaseChannelParams');
export const SetBootServerReleaseChannelSchema = z
    .object({
    /** A registered release to offer on the channel, or null to offer none. */
    currentVersion: BootServerVersionSchema.nullable(),
})
    .openapi('SetBootServerReleaseChannel', { description: 'Make a registered boot server release current on a channel, or none.' });
export const setBootServerReleaseChannelRoute = createRoute({
    method: 'put',
    path: '/v1/hq/boot-server-release-channels/{channel}',
    operationId: 'setBootServerReleaseChannel',
    tags: ['hq'],
    summary: 'Make a boot server release current on a channel',
    description: 'Boot servers on the channel learn of it at their next check (at start and every 6 hours), download and verify it, ' +
        'and show it as an update waiting for staff — nothing installs by itself. `currentVersion` must be a registered ' +
        'release (`422` otherwise); null offers none. A server never downgrades by this: it takes only a newer version. One ' +
        'platform audit row. Platform scope: requires `hq:releases` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_RELEASES,
    request: {
        params: BootServerReleaseChannelParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: SetBootServerReleaseChannelSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(BootServerReleaseChannelStateSchema, 'The channel, set.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        422: problemResponse('The version is not a registered boot server release.', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// The boot server's own
// ---------------------------------------------------------------------------------------------
export const BootServerReleaseQuerySchema = z
    .object({
    /** The version the server runs, so the answer can carry its rollback package. */
    installedVersion: BootServerVersionSchema.optional(),
})
    .openapi('BootServerReleaseQuery');
export const BootServerReleaseOfferSchema = z
    .object({
    /** The venue's `boot.serverUpdateChannel`. */
    channel: BootServerReleaseChannelSchema,
    /** The release current on the channel, or null when none is. The server takes it only when it is newer than its own. */
    version: BootServerVersionSchema.nullable(),
    /** Its signed manifest (verify it against the pinned release keys before anything else), or null. */
    signedManifest: CompactJwsSchema.nullable(),
    /**
     * The signed manifest of `installedVersion` when that is a registered release: the MSI the server reinstalls if the
     * new version does not come back. Null when it is not registered (an update then has no rollback package).
     */
    rollbackSignedManifest: CompactJwsSchema.nullable(),
})
    .openapi('BootServerReleaseOffer', { description: 'The boot server release current on this server’s channel, signed (spec/25 §13.6).' });
export const getBootServerReleaseRoute = createRoute({
    method: 'get',
    path: '/v1/boot-server/release',
    operationId: 'getBootServerRelease',
    tags: ['boot'],
    summary: 'The boot server release current on this server’s channel (F-BOOT-23)',
    description: 'Boot-server-authenticated: `Authorization: Bearer <device token>` of a device of kind `boot_server`; anything else ' +
        'is `401`. The server asks at start and every 6 hours. It verifies `signedManifest` against the release keys pinned ' +
        'in its build, takes it only when newer than what it runs, downloads and checks the MSI, and waits for staff to ' +
        'press Update now. Nothing installs by itself (spec/25 §13.6).',
    request: { query: BootServerReleaseQuerySchema },
    responses: {
        200: jsonResponse(BootServerReleaseOfferSchema, 'The current release, or none.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: problemResponse('The organisation does not have the Clavian Boot add-on.', 'entitlement-required'),
        500: commonProblemResponses[500],
    },
});
/** The boot server release routes, in the order the document lists them. */
export const BOOT_RELEASE_ROUTES = [
    registerBootServerReleaseRoute,
    listBootServerReleasesRoute,
    setBootServerReleaseChannelRoute,
    getBootServerReleaseRoute,
];
//# sourceMappingURL=boot-releases.js.map